import { DashboardStats } from '@/types/dashboard';

// Language-specific mock data
const mockDataByLanguage = {
  en: {
    months: [
      { key: 'jan', label: 'Jan' },
      { key: 'feb', label: 'Feb' },
      { key: 'mar', label: 'Mar' },
      { key: 'apr', label: 'Apr' },
      { key: 'may', label: 'May' },
      { key: 'jun', label: 'Jun' },
    ],
    maintenanceData: [
      { type: 'Open', value: 5 },
      { type: 'In Progress', value: 3 },
      { type: 'On Hold', value: 2 },
      { type: 'Resolved', value: 8 },
      { type: 'Closed', value: 12 },
    ],
    recentActivities: [
      {
        title: 'New maintenance request submitted',
        timeKey: '2hours',
        timeValue: 2,
        timeUnit: 'hoursAgo'
      },
      {
        title: 'Payment received from John Doe',
        timeKey: '3hours',
        timeValue: 3,
        timeUnit: 'hoursAgo'
      },
      {
        title: 'New resident check-in completed',
        timeKey: '5hours',
        timeValue: 5,
        timeUnit: 'hoursAgo'
      },
      {
        title: 'Maintenance task completed',
        timeKey: '1day',
        timeValue: 1,
        timeUnit: 'dayAgo'
      },
      {
        title: 'Monthly report generated',
        timeKey: '1day',
        timeValue: 1,
        timeUnit: 'dayAgo'
      },
    ]
  },
  es: {
    months: [
      { key: 'jan', label: 'Ene' },
      { key: 'feb', label: 'Feb' },
      { key: 'mar', label: 'Mar' },
      { key: 'apr', label: 'Abr' },
      { key: 'may', label: 'May' },
      { key: 'jun', label: 'Jun' },
    ],
    maintenanceData: [
      { type: 'Abierto', value: 5 },
      { type: 'En Progreso', value: 3 },
      { type: 'En Espera', value: 2 },
      { type: 'Resuelto', value: 8 },
      { type: 'Cerrado', value: 12 },
    ],
    recentActivities: [
      {
        title: 'Nueva solicitud de mantenimiento enviada',
        timeKey: '2hours',
        timeValue: 2,
        timeUnit: 'hoursAgo'
      },
      {
        title: 'Pago recibido de John Doe',
        timeKey: '3hours',
        timeValue: 3,
        timeUnit: 'hoursAgo'
      },
      {
        title: 'Check-in de nuevo residente completado',
        timeKey: '5hours',
        timeValue: 5,
        timeUnit: 'hoursAgo'
      },
      {
        title: 'Tarea de mantenimiento completada',
        timeKey: '1day',
        timeValue: 1,
        timeUnit: 'dayAgo'
      },
      {
        title: 'Reporte mensual generado',
        timeKey: '1day',
        timeValue: 1,
        timeUnit: 'dayAgo'
      },
    ]
  },
  pt: {
    months: [
      { key: 'jan', label: 'Jan' },
      { key: 'feb', label: 'Fev' },
      { key: 'mar', label: 'Mar' },
      { key: 'apr', label: 'Abr' },
      { key: 'may', label: 'Mai' },
      { key: 'jun', label: 'Jun' },
    ],
    maintenanceData: [
      { type: 'Aberto', value: 5 },
      { type: 'Em Progresso', value: 3 },
      { type: 'Em Espera', value: 2 },
      { type: 'Resolvido', value: 8 },
      { type: 'Fechado', value: 12 },
    ],
    recentActivities: [
      {
        title: 'Nova solicitação de manutenção enviada',
        timeKey: '2hours',
        timeValue: 2,
        timeUnit: 'hoursAgo'
      },
      {
        title: 'Pagamento recebido de John Doe',
        timeKey: '3hours',
        timeValue: 3,
        timeUnit: 'hoursAgo'
      },
      {
        title: 'Check-in de novo residente concluído',
        timeKey: '5hours',
        timeValue: 5,
        timeUnit: 'hoursAgo'
      },
      {
        title: 'Tarefa de manutenção concluída',
        timeKey: '1day',
        timeValue: 1,
        timeUnit: 'dayAgo'
      },
      {
        title: 'Relatório mensal gerado',
        timeKey: '1day',
        timeValue: 1,
        timeUnit: 'dayAgo'
      },
    ]
  }
};

export const dashboardStats: DashboardStats = {
  totalProperties: 150,
  totalResidents: 450,
  totalEmployees: 25,
  activeMaintenance: 12,
  vacantUnits: 8,
  overduePayments: 15,
  financeSummary: {
    totalRevenue: 125000,
    totalExpenses: 85000,
    netIncome: 40000
  },
  occupancyData: [
    { type: 'Occupied Units', value: 28 },
    { type: 'Vacant Units', value: 4 }
  ],
  financeData: [
    { month: 'jan', revenue: 150000, expenses: 120000 },
    { month: 'feb', revenue: 160000, expenses: 125000 },
    { month: 'mar', revenue: 155000, expenses: 130000 },
    { month: 'apr', revenue: 165000, expenses: 128000 },
    { month: 'may', revenue: 170000, expenses: 132000 },
    { month: 'jun', revenue: 175000, expenses: 135000 },
  ],
  rentCollection: 85,
  maintenanceData: mockDataByLanguage.en.maintenanceData,
  recentActivities: [
    {
      title: 'New maintenance request submitted',
      time: '2 hours ago',
    },
    {
      title: 'Payment received from John Doe',
      time: '3 hours ago',
    },
    {
      title: 'New resident check-in completed',
      time: '5 hours ago',
    },
    {
      title: 'Maintenance task completed',
      time: '1 day ago',
    },
    {
      title: 'Monthly report generated',
      time: '1 day ago',
    },
  ]
};

// Function to get language-specific dashboard data
export const getDashboardStatsByLanguage = (language: string, t: (key: string) => string): DashboardStats => {
  const langData = mockDataByLanguage[language as keyof typeof mockDataByLanguage] || mockDataByLanguage.en;
  
  // Create finance data with translated month labels
  const financeData = dashboardStats.financeData.map(item => ({
    ...item,
    month: langData.months.find(m => m.key === item.month)?.label || item.month
  }));
  
  // Create recent activities with translated time
  const recentActivities = langData.recentActivities.map(activity => ({
    title: activity.title,
    time: `${activity.timeValue} ${t(`dashboard:time.${activity.timeUnit}`)}`
  }));
  
  return {
    ...dashboardStats,
    financeData,
    maintenanceData: langData.maintenanceData,
    recentActivities
  };
}; 